import { getRouteApi, useLocation } from '@tanstack/react-router';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import { env } from '@/constants/env';
import { NavTabLink, NavTabs } from '@/components/new-ui/nav-tabs';
import { useScrollableRow } from '@/hooks/use-scrollable-row';
import { cn } from '@/lib/utils';
import { parseStringToBool } from '@/utils/parseStringToBool';
import { useFlag } from '@/hooks/use-flag';

import { canRead } from '@/features/permissions/resource-permissions';

export type DashboardNavProps = React.ComponentProps<'nav'> & { type: 'estate' | 'project' };

export const DashboardNav = ({ className, type, ...props }: DashboardNavProps) => {
  const { t } = useTranslation();
  const route = getRouteApi(
    type === 'estate'
      ? '/_authenticated/_sidebar-layout/estates/$estateId/_dashboard-layout'
      : '/_authenticated/_sidebar-layout/projects/$projectId/_dashboard-layout'
  );
  const params = route.useParams();
  const { pathname } = useLocation();
  const row = useScrollableRow(pathname);

  const data = route.useLoaderData();

  const permissions = data.metadata?.permissions;
  const canReadGeneral = canRead(permissions, 'general');
  const canReadMedia = canRead(permissions, 'media');
  const showPricehubble = parseStringToBool(env.SHOW_PRICEHUBBLE);
  const canReadDocuments =
    canRead(permissions, 'documentsInternal') || canRead(permissions, 'documentsExternal');
  const isAssetCatalogEnabled = useFlag('asset-catalog');

  return (
    <NavTabs
      ref={row.ref}
      style={row.style}
      aria-label={t('entitySectionsNavLabel')}
      className={cn(
        'no-scrollbar flex w-full justify-start overflow-x-auto [&>*]:flex-1',
        className
      )}
      {...props}
    >
      {params.projectId ? (
        <>
          <NavTabLink
            to="/projects/$projectId"
            activeOptions={{
              exact: true,
            }}
            hide={!canReadGeneral}
          >
            {t('overview')}
          </NavTabLink>
          <NavTabLink to="/projects/$projectId/objects" hide={!canReadGeneral}>
            {t('estates')}
          </NavTabLink>
          <NavTabLink to="/projects/$projectId/media" hide={!canReadMedia}>
            {t('mediaDatabase')}
          </NavTabLink>
          <NavTabLink to="/projects/$projectId/flatfinder" hide={!canReadGeneral || !canReadMedia}>
            {t('flatfinderFormFlatfinder')}
          </NavTabLink>
          <NavTabLink to="/projects/$projectId/documents" hide={!canReadDocuments}>
            {t('documents')}
          </NavTabLink>
          <NavTabLink
            to="/projects/$projectId/furniture-catalog"
            hide={!canReadGeneral || !isAssetCatalogEnabled}
          >
            {t('catalog.title')}
          </NavTabLink>
        </>
      ) : (
        <>
          <NavTabLink
            to="/estates/$estateId"
            activeOptions={{
              exact: true,
            }}
            hide={!canReadGeneral}
          >
            {t('overview')}
          </NavTabLink>
          <NavTabLink to="/estates/$estateId/media" hide={!canReadMedia}>
            {t('mediaDatabase')}
          </NavTabLink>
          <NavTabLink to="/estates/$estateId/analytics" hide={!canReadGeneral || !showPricehubble}>
            {t('analytics')}
          </NavTabLink>
          <NavTabLink to="/estates/$estateId/documents" hide={!canReadDocuments}>
            {t('documents')}
          </NavTabLink>
          <NavTabLink
            to="/estates/$estateId/furniture-catalog"
            hide={!canReadGeneral || !isAssetCatalogEnabled}
          >
            {t('catalog.title')}
          </NavTabLink>
        </>
      )}
    </NavTabs>
  );
};
